import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CircleIcon from '@mui/icons-material/Circle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CallEndIcon from '@mui/icons-material/CallEnd';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';
import { lightColors, themeBackgrounds } from '../../theme/theme';
import { Sampark, CHAT_CHANNEL, CHAT_ACTOR } from '@sampark-rtc/sampark-sdk';

interface ApiKeyData {
  appId: string;
  secretKey: string;
}

type SessionStatus = 'assigned' | 'active' | 'hold' | 'reconnecting' | 'closed';
type Presence = 'online' | 'away' | 'offline';
type Sender = 'agent' | 'customer' | 'system';
type MessageType = 'text' | 'image' | 'file' | 'video' | 'audio'| 'system';

interface MediaFileItem {
  fileId?: string;
  originalName?: string;
  attachmentUrl?: string;
  mimeType?: string;
}

interface ChatRoom {
  id: string;
  roomId: string;
  visitorId: string;
  customerName: string;
  topic: string;
  lastMessage: string;
  lastMessageAt: number;
  unreadCount: number;
  status: SessionStatus;
  presence: Presence;
  avatarColor: string;
}

interface ChatMessage {
  id: string;
  roomId: string;
  sender: Sender;
  text: string;
  /** Server timestamp string (same shape demo peer chat uses for bubble time). */
  sentAt: string;
  messageType?: MessageType;
  fileUrl?: string;
  fileName?: string;
  fileId?: string;
  files?: MediaFileItem[];
}

const AGENT_INBOX_LISTENER_ID = 'AGENT_BOT_INBOX';
let agentHumanChatListenersBound = false;

const setRoomsRef: { current: React.Dispatch<React.SetStateAction<ChatRoom[]>> } = {
  current: () => undefined,
};
const setMessagesRef: { current: React.Dispatch<React.SetStateAction<ChatMessage[]>> } = {
  current: () => undefined,
};
const setSelectedRoomIdRef: { current: React.Dispatch<React.SetStateAction<string | null>> } = {
  current: () => undefined,
};
const currentSessionIdRef: { current: string | null } = { current: null };
const currentRoomIdRef: { current: string | null } = { current: null };
const currentVisitorIdRef: { current: string | null } = { current: null };
const agentUserIdRef: { current: string } = { current: '' };
const setTypingUserRef: { current: React.Dispatch<React.SetStateAction<string | null>> } = {
  current: () => undefined,
};

const presenceColor: Record<Presence, string> = {
  online: '#22c55e',
  away: '#f59e0b',
  offline: '#94a3b8',
};

const statusChipStyles: Record<SessionStatus, { bg: string; color: string; label: string }> = {
  assigned: { bg: 'rgba(245, 158, 11, 0.14)', color: '#b45309', label: 'Approve to join' },
  active: { bg: 'rgba(0, 122, 255, 0.12)', color: '#007aff', label: 'Live' },
  hold: { bg: 'rgba(245, 158, 11, 0.14)', color: '#b45309', label: 'On hold' },
  reconnecting: { bg: 'rgba(148, 163, 184, 0.18)', color: '#475569', label: 'Reconnecting' },
  closed: { bg: 'rgba(239, 68, 68, 0.12)', color: '#dc6b6b', label: 'Closed' },
};

const formatListTime = (timestamp: number): string => {
  if (!timestamp) return '';
  const diffMinutes = Math.max(1, Math.round((Date.now() - timestamp) / 60000));
  if (diffMinutes < 60) return `${diffMinutes}m`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays}d`;
};

/** Same bubble-time formatting as UI kit demo `getTimeFromTimestamp`. */
const formatBubbleTime = (timestamp?: string): string => {
  if (!timestamp) return '';
  const timePart = String(timestamp).split('T')[1];
  if (!timePart) return '';
  const [hoursStr, minutes] = timePart.split(':');
  let hours = parseInt(hoursStr, 10);
  if (!Number.isFinite(hours) || !minutes) return '';
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${ampm}`;
};

const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const mapSessionStatus = (status?: string): SessionStatus => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'live' || normalized === 'active') return 'active';
  if (normalized === 'agent_hold' || normalized === 'hold') return 'hold';
  if (normalized === 'reconnecting') return 'reconnecting';
  if (normalized === 'closed') return 'closed';
  return 'assigned';
};

/** Prior agent must not see Live after handoff — session can still be active for another agent. */
const resolveSessionStatusForAgent = (
  session: any,
  agentId?: string,
  previousStatus?: SessionStatus
): SessionStatus => {
  if (previousStatus === 'closed') return 'closed';
  const me = String(agentId || agentUserIdRef.current || '');
  const assigned = session?.assigned_agent ? String(session.assigned_agent) : '';
  if (me && assigned && assigned !== me) return 'closed';
  if (me && session?.last_assigned_agent === me && assigned !== me) return 'closed';
  return mapSessionStatus(session?.status);
};

const presenceForStatus = (status: SessionStatus): Presence => {
  if (status === 'active') return 'online';
  if (status === 'assigned' || status === 'hold') return 'away';
  return 'offline';
};

const toTimestamp = (value: unknown): number => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = new Date(value as string | number).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const toChatRoom = (
  session: any,
  fallbackRoomId: string,
  fallbackVisitorId: string,
  colorIndex: number
): ChatRoom => {
  const sessionId = String(session?.session_id || '');
  const roomId = String(session?.room_id || fallbackRoomId || '');
  const visitorId = String(session?.visitor_id || fallbackVisitorId || '');
  const status = resolveSessionStatusForAgent(session);
  const rawName = String(session?.visitor_name || '');
  const looksBad =
    !rawName ||
    rawName.startsWith('visitor_') ||
    (rawName.includes(':') && /==/.test(rawName));
  const customerName = looksBad
    ? (sessionId ? `Session ${sessionId}` : 'Visitor')
    : rawName;
  const lastMessage = String(session?.last_message || '');
  const lastAt = session?.last_message_at || session?.updated_at || session?.created_at;

  return {
    id: sessionId,
    roomId,
    visitorId,
    customerName,
    topic: 'Human handoff',
    lastMessage: lastMessage
      || (status === 'assigned' ? 'Waiting for you to join' : '')
      || (status === 'hold' ? 'On hold' : '')
      || (status === 'closed' ? 'Conversation closed' : ''),
    lastMessageAt: lastAt ? toTimestamp(lastAt) : Date.now(),
    unreadCount: Number(session?.unread_count || 0),
    status,
    presence: presenceForStatus(status),
    avatarColor: lightColors[Math.abs(colorIndex) % lightColors.length],
  };
};

const flattenAgentRooms = (payload: any): ChatRoom[] => {
  if (!Array.isArray(payload)) return [];

  const rooms: ChatRoom[] = [];
  payload.forEach((item, index) => {
    if (Array.isArray(item?.sessions)) {
      const roomId = String(item.room_id || '');
      const visitorId = String(item.visitor_id || '');
      const visitorName = item.visitor_name || '';
      item.sessions.forEach((session: any, sessionIndex: number) => {
        const room = toChatRoom(
          {
            ...session,
            room_id: session?.room_id || roomId,
            visitor_id: session?.visitor_id || visitorId,
            visitor_name: session?.visitor_name || visitorName,
          },
          roomId,
          visitorId,
          index + sessionIndex
        );
        if (room.id) rooms.push(room);
      });
      return;
    }

    if (item?.session_id) {
      const room = toChatRoom(item, item.room_id, item.visitor_id, index);
      if (room.id) rooms.push(room);
    }
  });
  return rooms;
};

const isBotFlaggedMessage = (source: any): boolean =>
  source?.bot_messages === true;

/** Visitor stays left; bot (`bot_messages`) + human agent both render on the right. */
const resolveMessageSender = (
  senderId: string,
  agentUserId: string,
  visitorId: string,
  botMessages = false
): Sender => {
  if (botMessages) return 'agent';
  if (senderId && visitorId && senderId === visitorId) return 'customer';
  if (senderId && agentUserId && senderId === agentUserId) return 'agent';
  // History stores bot replies under the bot user id, not the logged-in agent.
  if (senderId && visitorId && senderId !== visitorId) return 'agent';
  return 'customer';
};

const isRenderableMediaUrl = (url?: string | null): boolean => {
  if (!url || typeof url !== 'string') return false;
  const u = url.trim();
  return (
    u.startsWith('https://') ||
    u.startsWith('http://') ||
    u.startsWith('blob:') ||
    u.startsWith('data:')
  );
};

const resolveRenderableMediaUrl = (item: any): string | undefined => {
  if (!item) return undefined;
  if (typeof item === 'string') return isRenderableMediaUrl(item) ? item : undefined;
  const url = item.attachmentUrl || item.url || item.downloadUrl;
  return isRenderableMediaUrl(url) ? String(url).trim() : undefined;
};

const normalizeMediaFiles = (raw: any, fallbacks?: any[]): MediaFileItem[] => {
  if (!raw && (!fallbacks || !fallbacks.length)) return [];
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const out: MediaFileItem[] = [];
  const count = Math.max(list.length, fallbacks?.length || 0);
  for (let i = 0; i < count; i++) {
    const item = list[i] || {};
    const fallback = fallbacks?.[i];
    out.push({
      fileId: item.fileId,
      originalName: item.originalName || fallback?.originalName,
      attachmentUrl:
        resolveRenderableMediaUrl(item) ||
        resolveRenderableMediaUrl(fallback),
      mimeType: item.mimeType || fallback?.mimeType,
    });
  }
  return out;
};

const mediaFilesFromMessage = (msg: any, fileInfo: any): MediaFileItem[] => {
  const attachments = Array.isArray(msg?.data?.attachments) ? msg.data.attachments : undefined;
  const fallbacks =
    attachments ||
    (msg?.getUrl?.() ? [{ url: msg.getUrl() }] : undefined);
  return normalizeMediaFiles(fileInfo, fallbacks);
};

const isImageMediaList = (files: MediaFileItem[], rawType: string): boolean => {
  if (rawType === 'image') return true;
  if (!files.length) return false;
  for (let i = 0; i < files.length; i++) {
    const mime = String(files[i].mimeType || '');
    if (!mime.startsWith('image/')) return false;
  }
  return Boolean(files[0].mimeType);
};

const mapHistoryChats = (
  chats: any[],
  sessionId: string,
  agentUserId: string,
  visitorId: string
): ChatMessage[] =>
  (chats || []).map((chat, index) => {
    const senderId = String(chat?.user || '');
    const fileInfo = chat?.file_info;
    const files = normalizeMediaFiles(fileInfo, chat?.url ? [{ url: chat.url }] : undefined);
    // const rawType = chat.message_type || (files.length ? 'file' : 'text');
    // const isImage = isImageMediaList(files, rawType);
    const text = String(chat?.chat || '');
    const first = files[0];

    return {
      id: String(chat?.message_id || `hist-${sessionId}-${index}`),
      roomId: sessionId,
      sender: resolveMessageSender(senderId, agentUserId, visitorId, isBotFlaggedMessage(chat)),
      text: text || first?.originalName || '',
      sentAt: String(chat?.created_at || ''),
      messageType: chat.message_type as MessageType,
      fileUrl: first?.attachmentUrl,
      fileName: first?.originalName,
      fileId: first?.fileId,
      files,
    };
  });

const mapSdkMessage = (msg: any, sessionId: string, agentUserId: string, visitorId: string): ChatMessage => {
  const senderId = String(msg?.getSenderId?.() || msg?.senderId || '');
  const fileInfo = Sampark.ChatBot.getMessageFileInfo?.(msg) || msg?.getFileInfo?.();
  const files = mediaFilesFromMessage(msg, fileInfo);
  const isFile = Boolean(Sampark.ChatBot.isFileMessage?.(msg) || files.length);
  const rawType = msg.messageType || (files.length ? 'file' : 'text');
  const isImage = isImageMediaList(files, rawType);
  const caption = String(msg?.getText?.() || '');
  const first = files[0];

  return {
    id: String(msg?.getId?.() || msg?.id || ''),
    roomId: sessionId,
    sender: resolveMessageSender(senderId, agentUserId, visitorId, isBotFlaggedMessage(msg)),
    text: caption,
    sentAt: String(msg?.getSentAt?.() || msg?.sentAt || msg?.getSent?.()?.updatedAt || ''),
    messageType: isFile ? (isImage ? 'image' : (rawType as MessageType) || 'file') : 'text',
    fileUrl: first?.attachmentUrl || msg?.getUrl?.(),
    fileName: first?.originalName,
    fileId: first?.fileId,
    files,
  };
};

const getMessageSessionId = (msg: any): string =>
  String(msg?.session_id || msg?.getSessionId?.() || '');

const AgentChats: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationId = useSelector((state: any) => state.applicationDetails.app_id);
  const [apiKeys, setApiKeys] = useState<ApiKeyData | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isOpeningSession, setIsOpeningSession] = useState(false);
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [listTab, setListTab] = useState<'active' | 'hold' | 'closed'>('active');
  const [draft, setDraft] = useState('');
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  const [mediaPreview, setMediaPreview] = useState<{
    items: MediaFileItem[];
    index: number;
    caption?: string;
  } | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingPreviewUrls, setPendingPreviewUrls] = useState<string[]>([]);
  const [isDraggingMedia, setIsDraggingMedia] = useState(false);
  const [pendingCloseSessionId, setPendingCloseSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const atBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const openGenerationRef = useRef(0);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragDepthRef = useRef(0);

  setRoomsRef.current = setRooms;
  setMessagesRef.current = setMessages;
  setSelectedRoomIdRef.current = setSelectedRoomId;
  setTypingUserRef.current = setTypingUser;
  agentUserIdRef.current = userDetails?.user_id || '';

  useEffect(() => {
    setApiKeys(null);

    const fetchApiKeys = async () => {
      const payload = {
        request_id: 'notegtest1',
        timestamp: '2024-08-02T14:48:00Z',
        action: 'get_user_credentials',
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: applicationId || '',
          user_id: userDetails?.user_id || '',
        }, REQ_ENC_KEY),
      };

      type ApiResponse = { status: number; [key: string]: any };
      try {
        const response = await httpService.post<ApiResponse>(`${API_BASE_URL}/user/getUserCredentials`, payload);
        if (response.data.status === 200 && response.data.data) {
          const data = response.data.data;
          const decrypted = await decryptResponse<{
            appId: string;
            secretKey: string;
          }>(data, getSessionKey() as string);
          const { appId, secretKey } = decrypted;
          setApiKeys({
            appId: appId,
            secretKey: secretKey,
          });
        } else {
          setApiKeys(null);
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not load application credentials');
      }
    };

    fetchApiKeys();
  }, [applicationId]);

  useEffect(() => {
    if (!apiKeys?.appId || !apiKeys?.secretKey) {
      return;
    }

    let cancelled = false;
    setSdkReady(false);

    const initializeSampark = async () => {
      try {
          try { await Sampark.logout(); } catch { /* ignore */ }

        const sampark = await Sampark.init(apiKeys.appId, apiKeys.secretKey);
        if (cancelled) return;
        if (sampark && userDetails?.user_id) {
            const loginData = await Sampark.login(userDetails?.user_id);
            if (!cancelled && loginData) {
              setSdkReady(true);
            }
        } else if (!cancelled && !userDetails?.user_id) {
            toast.error('Agent user id is required to start desk chat');
        }
      } catch (error) {
        if (!cancelled) {
          // toast.error(error instanceof Error ? error.message : 'Sampark init failed');
          console.error(error);
        }
      }
    };

    initializeSampark();

    return () => {
      cancelled = true;
      setSdkReady(false);
    };
  }, [apiKeys?.appId, apiKeys?.secretKey]);

  const upsertInboxRoom = useCallback((incoming: ChatRoom) => {
    setRoomsRef.current((current) => {
      const existing = current.find((room) => room.id === incoming.id);
      const nextRoom = existing
        ? {
            ...existing,
            ...incoming,
            unreadCount: incoming.unreadCount || existing.unreadCount,
            lastMessage: incoming.lastMessage || existing.lastMessage,
            avatarColor: existing.avatarColor,
          }
        : incoming;
      return [nextRoom, ...current.filter((room) => room.id !== incoming.id)];
    });
  }, []);

  const applyUnread = useCallback((roomId?: string | null, sessionId?: string | null, unreadCount?: number) => {
    if (unreadCount == null) return;
    setRoomsRef.current((current) =>
      current.map((room) => {
        if (sessionId) {
          if (room.id !== String(sessionId)) return room;
        } else if (roomId) {
          if (room.roomId !== String(roomId)) return room;
        } else {
          return room;
        }
        return { ...room, unreadCount: Number(unreadCount) || 0 };
      })
    );
  }, []);

  useEffect(() => {
    if (!sdkReady) return;

    try {
      Sampark.ChatBot.joinStatusRoom();
    } catch {
      return;
    }

    if (!agentHumanChatListenersBound) {
      agentHumanChatListenersBound = true;

      Sampark.ChatBot.onSessionSwitched((payload: any) => {
        const sessionId = String(payload?.session_id || '');
        if (!sessionId) return;
        currentSessionIdRef.current = sessionId;
        if (payload?.room_id) currentRoomIdRef.current = String(payload.room_id);
        const visitorId = String(payload?.session?.visitor_id || currentVisitorIdRef.current || '');
        if (visitorId) currentVisitorIdRef.current = visitorId;

        const status = resolveSessionStatusForAgent(
          payload?.session,
          agentUserIdRef.current,
          undefined
        );
        setRoomsRef.current((current) =>
          current.map((room) =>
            room.id === sessionId
              ? {
                  ...room,
                  status,
                  presence: presenceForStatus(status),
                  visitorId: visitorId || room.visitorId,
                  lastMessage:
                    status === 'closed'
                      ? (room.lastMessage || 'Conversation closed')
                      : room.lastMessage,
                }
              : room
          )
        );
      });

      Sampark.ChatBot.onVisitorAssigned((payload: any) => {
        const session = payload?.session || payload;
        const sessionId = String(payload?.session_id || session?.session_id || '');
        const colorIndex = Array.from(sessionId).reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
        const room = toChatRoom(
          {
            ...session,
            session_id: sessionId,
            room_id: payload?.room_id || session?.room_id,
            visitor_id: payload?.visitor_id || session?.visitor_id,
            visitor_name: payload?.visitor_name || session?.visitor_name,
            assigned_agent: payload?.agent_id || session?.assigned_agent,
            status: payload?.status || session?.status || 'assigned',
            title: session?.title,
          },
          payload?.room_id,
          payload?.visitor_id,
          colorIndex
        );
        if (room.id) upsertInboxRoom(room);
      });

      Sampark.ChatBot.onUserLogin((payload: any) => {
        const sessionId = payload?.session_id ? String(payload.session_id) : '';
        const visitorId = payload?.visitor_id ? String(payload.visitor_id) : '';
        const visitorName = payload?.visitor_name ? String(payload.visitor_name) : '';
        // Only the live converted session — do not rename closed guest chats.
        if (!visitorName || !sessionId) return;
        setRoomsRef.current((current) =>
          current.map((room) => {
            if (room.id !== sessionId) return room;
            return {
              ...room,
              customerName: visitorName,
              visitorId: visitorId || room.visitorId,
            };
          })
        );
      });

      Sampark.ChatBot.onAgentJoinedChat((payload: any) => {
        const sessionId = String(payload?.session_id || '');
        if (payload?.rejected) {
          setRoomsRef.current((current) => current.filter((room) => room.id !== sessionId));
          if (sessionId && sessionId === currentSessionIdRef.current) {
            currentSessionIdRef.current = null;
            currentRoomIdRef.current = null;
            currentVisitorIdRef.current = null;
            setSelectedRoomIdRef.current(null);
            setMessagesRef.current([]);
            setTypingUserRef.current(null);
          }
          return;
        }
        if (sessionId !== currentSessionIdRef.current) return;
        setRoomsRef.current((current) =>
          current.map((room) =>
            room.id === sessionId
              ? { ...room, status: 'active', presence: 'online' }
              : room
          )
        );
      });

      Sampark.ChatBot.onAgentJoinedChatError((payload: any) => {
        toast.error(payload?.error || payload?.reason || payload?.code || 'Failed to join or reject chat');
      });

      Sampark.ChatBot.onAgentChatHold((payload: any) => {
        const sessionId = String(payload?.session_id || '');
        if (!sessionId) return;
        setRoomsRef.current((current) =>
          current.map((room) =>
            room.id === sessionId
              ? { ...room, status: 'hold', presence: presenceForStatus('hold'), lastMessage: 'On hold' }
              : room
          )
        );
      });
      Sampark.ChatBot.onAgentChatHoldError((payload: any) => {
        toast.error(payload?.error || payload?.reason || payload?.code || 'Failed to hold chat');
      });
      Sampark.ChatBot.onAgentChatResume((payload: any) => {
        const sessionId = String(payload?.session_id || '');
        if (!sessionId) return;
        setRoomsRef.current((current) =>
          current.map((room) =>
            room.id === sessionId
              ? { ...room, status: 'active', presence: presenceForStatus('active'), lastMessage: 'Conversation resumed' }
              : room
          )
        );
      });
      Sampark.ChatBot.onAgentChatResumeError((payload: any) => {
        toast.error(payload?.error || payload?.reason || payload?.code || 'Failed to resume chat');
      });

      Sampark.ChatBot.onActivity(() => {
        const sessionId = currentSessionIdRef.current;
        if (!sessionId) return;
        // setPendingCloseSessionId(sessionId);
        try {
          Sampark.ChatBot.closeChat();
        } catch {
          setPendingCloseSessionId(null);
        }
      });

      Sampark.ChatBot.onChatClosed((payload: any) => {
        const closedId = String(payload?.session_id || '');
        // setPendingCloseSessionId((current) => (current === closedId ? null : current));
        setRoomsRef.current((current) =>
          current.map((room) =>
            room.id === closedId
              ? { ...room, status: 'closed', presence: presenceForStatus('closed'), lastMessage: 'Conversation closed' }
              : room
          )
        );
        if (closedId && closedId === currentSessionIdRef.current) {
          setTypingUserRef.current(null);
        }
      });

      Sampark.ChatBot.onCloseChatError((payload: any) => {
        setPendingCloseSessionId(null);
        toast.error(payload?.error || payload?.reason || payload?.code || 'Failed to close chat');
      });

      Sampark.ChatBot.onAgentCloseResponse((payload: any) => {
        const sessionId = String(payload?.session_id || '');
        if (String(payload?.action || '').toLowerCase() !== 'another_agent') return;
        // setPendingCloseSessionId((current) => (current === sessionId ? null : current));
        // setRoomsRef.current((current) => current.filter((room) => room.id !== sessionId));
        if (sessionId && sessionId === currentSessionIdRef.current) {
          currentSessionIdRef.current = null;
          currentRoomIdRef.current = null;
          currentVisitorIdRef.current = null;
          setSelectedRoomIdRef.current(null);
          setMessagesRef.current([]);
          setTypingUserRef.current(null);
        }
      });

      Sampark.ChatBot.onAgentCloseResponseError((payload: any) => {
        setPendingCloseSessionId(null);
        toast.error(payload?.error || payload?.reason || payload?.code || 'Visitor close response failed');
      });

      Sampark.ChatBot.onQueueCancelled((payload: any) => {
        const sessionId = String(payload?.session_id || '');
        if (!sessionId) return;
        setRoomsRef.current((current) => current.filter((room) => room.id !== sessionId));
        if (sessionId === currentSessionIdRef.current) {
          currentSessionIdRef.current = null;
          currentRoomIdRef.current = null;
          currentVisitorIdRef.current = null;
          setSelectedRoomIdRef.current(null);
          setMessagesRef.current([]);
          setTypingUserRef.current(null);
        }
        toast.info('Visitor left the waiting room');
      });

      Sampark.ChatBot.onAgentDisconnected((payload: any) => {
        if (String(payload?.session_id) !== currentSessionIdRef.current) return;
        setRoomsRef.current((current) =>
          current.map((room) =>
            room.id === currentSessionIdRef.current
              ? { ...room, status: 'reconnecting', presence: 'offline' }
              : room
          )
        );
      });

      Sampark.ChatBot.onAgentReconnected(async (payload: any) => {
        if (String(payload?.session_id) !== currentSessionIdRef.current) return;
        const status = mapSessionStatus(payload?.status || 'active');
        setRoomsRef.current((current) =>
          current.map((room) =>
            room.id === currentSessionIdRef.current
              ? { ...room, status, presence: presenceForStatus(status) }
              : room
          )
        );

        const sessionId = currentSessionIdRef.current;
        const roomId = currentRoomIdRef.current;
        if (!sessionId || !roomId) return;
        try {
          const result = await Sampark.ChatBot.switchSession(sessionId, roomId);
          if (String(result?.session_id) !== currentSessionIdRef.current) return;
          const visitorId = String(
            result?.session?.visitor_id || currentVisitorIdRef.current || ''
          );
          if (visitorId) currentVisitorIdRef.current = visitorId;
          if (Array.isArray(result?.chats)) {
            setMessagesRef.current(
              mapHistoryChats(result.chats, sessionId, agentUserIdRef.current, visitorId)
            );
          }
        } catch {
          /* status already restored; media refresh is best-effort */
        }
      });
    }

    Sampark.addChatBotMessageListener(AGENT_INBOX_LISTENER_ID, {
      onTextMessageReceived: (msg: any) => {
        const sessionId = getMessageSessionId(msg) || currentSessionIdRef.current;
        if (sessionId && currentSessionIdRef.current && String(sessionId) !== currentSessionIdRef.current) {
          return;
        }
        const mapped = mapSdkMessage(msg, currentSessionIdRef.current || String(sessionId || ''), agentUserIdRef.current, currentVisitorIdRef.current || '');
        if (!mapped.id) return;
        setMessagesRef.current((current) => {
          if (current.some((message) => message.id === mapped.id)) return current;
          return [...current, mapped];
        });
        setRoomsRef.current((current) =>
          current.map((room) =>
            room.id === currentSessionIdRef.current
              ? { ...room, lastMessage: mapped.text, lastMessageAt: toTimestamp(mapped.sentAt) }
              : room
          )
        );
      },
      onMediaMessageReceived: (msg: any) => {
        const sessionId = getMessageSessionId(msg) || currentSessionIdRef.current;
        if (sessionId && currentSessionIdRef.current && String(sessionId) !== currentSessionIdRef.current) {
          return;
        }
        const mapped = mapSdkMessage(msg, currentSessionIdRef.current || String(sessionId || ''), agentUserIdRef.current, currentVisitorIdRef.current || '');
        if (!mapped.id) return;
        setMessagesRef.current((current) => {
          if (current.some((message) => message.id === mapped.id)) return current;
          return [...current, mapped];
        });
        setRoomsRef.current((current) =>
          current.map((room) =>
            room.id === currentSessionIdRef.current
              ? {
                  ...room,
                  lastMessage: mapped.files?.[0]?.originalName || mapped.fileName || mapped.text || 'Attachment',
                  lastMessageAt: toTimestamp(mapped.sentAt),
                }
              : room
          )
        );
      },
      onIncomingMessageNotification: (data: any) => {
        const sessionId = String(data?.sessionId || '');
        const roomId = String(data?.roomId || '');
        const preview = String(
          data?.message ||
            (Array.isArray(data?.fileInfo)
              ? data.fileInfo[0]?.originalName
              : data?.fileInfo?.originalName) ||
            'New message'
        );
        setRoomsRef.current((current) => {
          const match = current.find((room) => (sessionId && room.id === sessionId) || room.roomId === roomId);
          if (!match) return current;
          const next = {
            ...match,
            lastMessage: preview,
            lastMessageAt: toTimestamp(data?.timestamp),
          };
          if (data?.unreadCount != null) {
            next.unreadCount = Number(data.unreadCount) || 0;
          }
          return [next, ...current.filter((room) => room.id !== match.id)];
        });
      },
      onUnreadCountUpdated: (data: any) => {
        if (data?.type !== 'bot') return;
        applyUnread(data?.roomId, data?.sessionId, data?.unreadCount);
      },
      onTypingStarted: (typingEvent: any) => {
        const sender = typingEvent.getSender?.() || typingEvent.sender;
        setTypingUserRef.current(sender?.getName?.() || sender?.name || 'Visitor');
      },
      onTypingEnded: () => {
        setTypingUserRef.current(null);
      },
    });

    const loadInbox = async () => {
      setInboxLoading(true);
      try {
        const sdkRooms = await Sampark.ChatBot.getAgentBotSessions();
        setRooms(flattenAgentRooms(sdkRooms));
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not load assigned chats');
        setRooms([]);
      } finally {
        setInboxLoading(false);
      }
    };

    loadInbox();

    return () => {
      Sampark.removeChatBotMessageListener(AGENT_INBOX_LISTENER_ID);
    };
  }, [sdkReady, applyUnread, upsertInboxRoom]);

  const selectedRoom = rooms.find((room) => room.id === selectedRoomId) || null;
  const showRoomList = isDesktop || !selectedRoomId;
  const showChatPane = isDesktop || Boolean(selectedRoomId);
  const composerUnlocked = selectedRoom?.status === 'active';

  const sortedRooms = useMemo(
    () => {
      const allClosed = rooms.length > 0 && rooms.every((room) => room.status === 'closed');
      const list = [...rooms].sort((a, b) => {
        const aNew = a.status === 'assigned' ? 0 : 1;
        const bNew = b.status === 'assigned' ? 0 : 1;
        if (aNew !== bNew) return aNew - bNew;
        const aClosed = a.status === 'closed' ? 1 : 0;
        const bClosed = b.status === 'closed' ? 1 : 0;
        if (aClosed !== bClosed) return aClosed - bClosed;
        return b.lastMessageAt - a.lastMessageAt;
      });
      return list.map((room, index) => ({
        room,
        badge:
          room.id === selectedRoomId
            ? (room.status === 'closed' ? null : 'Current')
            : room.status === 'assigned'
              ? 'New'
              : allClosed && index === 0
                ? 'Last active'
                : null,
      }));
    },
    [rooms, selectedRoomId]
  );
  const activeTabRooms = sortedRooms.filter(({ room }) => room.status !== 'closed' && room.status !== 'hold');
  const holdTabRooms = sortedRooms.filter(({ room }) => room.status === 'hold');
  const closedTabRooms = sortedRooms.filter(({ room }) => room.status === 'closed');
  const tabRooms = listTab === 'hold' ? holdTabRooms : listTab === 'closed' ? closedTabRooms : activeTabRooms;

  const roomMessages = useMemo(
    () => messages.filter((message) => message.roomId === selectedRoomId),
    [messages, selectedRoomId]
  );

  useEffect(() => {
    atBottomRef.current = true;
    prevMessageCountRef.current = 0;
    setShowScrollToBottom(false);
  }, [selectedRoomId]);

  useEffect(() => {
    const grew = roomMessages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = roomMessages.length;
    if (atBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (grew) {
      setShowScrollToBottom(true);
    }
  }, [roomMessages.length, selectedRoomId, typingUser]);

  const openSession = useCallback(async (room: ChatRoom) => {
    const generation = ++openGenerationRef.current;
    setIsOpeningSession(true);
    setSelectedRoomId(room.id);
    setMessages([]);
    setDraft('');
    setPendingFiles([]);
    setIsDraggingMedia(false);
    dragDepthRef.current = 0;
    setTypingUser(null);
    currentSessionIdRef.current = room.id;
    currentRoomIdRef.current = room.roomId;
    currentVisitorIdRef.current = room.visitorId;

    try {
      Sampark.PeerChat.joinroom(room.roomId, {
        channel: CHAT_CHANNEL.BOT,
        actor: CHAT_ACTOR.AGENT,
        sessionId: room.id,
      });

      const result = await Sampark.ChatBot.switchSession(room.id, room.roomId);
      if (generation !== openGenerationRef.current) return;

      const sessionId = String(result?.session_id || room.id);
      const roomId = String(result?.room_id || room.roomId);
      const visitorId = String(result?.session?.visitor_id || room.visitorId);
      currentSessionIdRef.current = sessionId;
      currentRoomIdRef.current = roomId;
      currentVisitorIdRef.current = visitorId;

      Sampark.ChatBot.markBotMessagesRead(roomId, sessionId);

      const status = resolveSessionStatusForAgent(
        result?.session || { status: room.status },
        agentUserIdRef.current,
        room.status
      );
      const visitorName = String(result?.session?.visitor_name || room.customerName || '');
      setRooms((current) =>
        current.map((item) =>
          item.id === room.id
            ? {
                ...item,
                id: sessionId,
                roomId,
                visitorId,
                customerName:
                  visitorName &&
                  !visitorName.startsWith('visitor_') &&
                  !(visitorName.includes(':') && /==/.test(visitorName))
                    ? visitorName
                    : item.customerName,
                status,
                presence: presenceForStatus(status),
                lastMessage:
                  status === 'closed'
                    ? (item.lastMessage || 'Conversation closed')
                    : item.lastMessage,
              }
            : item
        )
      );
      setSelectedRoomId(sessionId);
      if (Array.isArray(result?.chats)) {
        setMessages(mapHistoryChats(result.chats, sessionId, agentUserIdRef.current, visitorId));
      }
    } catch (error) {
      if (generation !== openGenerationRef.current) return;
      toast.error(error instanceof Error ? error.message : 'Could not open this conversation');
    } finally {
      if (generation === openGenerationRef.current) {
        setIsOpeningSession(false);
      }
    }
  }, []);

  const handleSelectRoom = (roomId: string): void => {
    const room = rooms.find((item) => item.id === roomId);
    if (!room) return;
    if (room.id === currentSessionIdRef.current && selectedRoomId === room.id) {
      return;
    }
    openSession(room);
  };

  const handleApproveJoin = (): void => {
    try {
      Sampark.ChatBot.agentJoinedChat();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not join this chat');
    }
  };

  const handleRejectJoin = (): void => {
    try {
      Sampark.ChatBot.rejectAssignedChat();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not reject this chat');
    }
  };

  const handleEndChat = (): void => {
    setHeaderMenuAnchor(null);
    const sessionId = currentSessionIdRef.current || selectedRoomId;
    if (!sessionId || selectedRoom?.status === 'closed' || pendingCloseSessionId === sessionId) return;
    try {
      // setPendingCloseSessionId(sessionId);
      Sampark.ChatBot.closeChat();
    } catch (error) {
      setPendingCloseSessionId(null);
      toast.error(error instanceof Error ? error.message : 'Could not close this chat');
    }
  };

  const handleHoldOrResume = (): void => {
    setHeaderMenuAnchor(null);
    if (!selectedRoom || selectedRoom.status === 'closed' || selectedRoom.status === 'assigned') return;
    try {
      if (selectedRoom.status === 'hold') {
        Sampark.ChatBot.resumeChat();
      } else {
        Sampark.ChatBot.holdChat();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update hold state');
    }
  };

  const handleSendMessage = async (): Promise<void> => {
    if (pendingFiles.length > 0) {
      await handleSendAttachment();
      return;
    }
    const text = draft.trim();
    const visitorId = currentVisitorIdRef.current || selectedRoom?.visitorId;
    if (!text || !selectedRoomId || !visitorId || !composerUnlocked || isSending) return;

    setIsSending(true);
    try {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      Sampark.ChatBot.endTyping(new Sampark.TypingIndicator(visitorId, Sampark.RECEIVER_TYPE.USER));
      const msg = new Sampark.TextMessage(
        visitorId,
        text,
        Sampark.RECEIVER_TYPE.USER,
      );
      await Sampark.ChatBot.sendMessage(msg);
      setDraft('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not send message');
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    const urls = pendingFiles.map((file) => URL.createObjectURL(file));
    setPendingPreviewUrls(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [pendingFiles]);

  const clearPendingFiles = (): void => {
    setPendingFiles([]);
  };

  const queuePendingFiles = (incoming: File[]): void => {
    if (!composerUnlocked || !incoming.length) return;
    const visitorId = currentVisitorIdRef.current || selectedRoom?.visitorId;
    if (!visitorId) return;
    const next = incoming.filter((file) => file.size > 0);
    if (!next.length) return;
    setPendingFiles((prev) => [...prev, ...next]);
  };

  const handlePickFile = (): void => {
    if (!composerUnlocked) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selected = event.target.files;
    const files: File[] = selected?.length ? Array.from(selected) : [];
    event.target.value = '';
    queuePendingFiles(files);
  };

  const filesFromDataTransfer = (dataTransfer: DataTransfer | null): File[] => {
    if (!dataTransfer) return [];
    if (dataTransfer.items?.length) {
      const fromItems: File[] = [];
      for (let i = 0; i < dataTransfer.items.length; i++) {
        const item = dataTransfer.items[i];
        if (item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (file && file.size > 0) fromItems.push(file);
      }
      if (fromItems.length) return fromItems;
    }
    return dataTransfer.files?.length ? Array.from(dataTransfer.files) : [];
  };

  const handleComposerPaste = (event: React.ClipboardEvent<HTMLElement>): void => {
    const files = filesFromDataTransfer(event.clipboardData);
    if (!files.length) return;
    event.preventDefault();
    queuePendingFiles(files);
  };

  const handleComposerDragEnter = (event: React.DragEvent<HTMLElement>): void => {
    if (!Array.from(event.dataTransfer.types).includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setIsDraggingMedia(true);
  };

  const handleComposerDragLeave = (event: React.DragEvent<HTMLElement>): void => {
    if (!Array.from(event.dataTransfer.types).includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingMedia(false);
  };

  const handleComposerDragOver = (event: React.DragEvent<HTMLElement>): void => {
    if (!Array.from(event.dataTransfer.types).includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleComposerDrop = (event: React.DragEvent<HTMLElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingMedia(false);
    queuePendingFiles(filesFromDataTransfer(event.dataTransfer));
  };

  const resolveMediaType = (file: File): MessageType =>
    file.type.startsWith('image/')
      ? 'image'
      : file.type.startsWith('video/')
        ? 'video'
        : file.type.startsWith('audio/')
          ? 'audio'
          : 'file';

  const handleSendAttachment = async (): Promise<void> => {
    const visitorId = currentVisitorIdRef.current || selectedRoom?.visitorId;
    const sessionId = currentSessionIdRef.current || selectedRoomId || '';
    if (!pendingFiles.length || !visitorId || !composerUnlocked || isSending) return;

    setIsSending(true);
    try {
      const caption = draft.trim();
      const mediaMessages = pendingFiles.map((file) => {
        const media = new Sampark.MediaMessage(
          visitorId,
          file,
          resolveMediaType(file),
          Sampark.RECEIVER_TYPE.USER,
        );
        if (caption) media.setText(caption);
        return media;
      });
      const sent = await Sampark.ChatBot.sendMediaMessage(
        mediaMessages.length === 1 ? mediaMessages[0] : mediaMessages,
      );
      const sentList = Array.isArray(sent) ? sent : sent ? [sent] : [];
      if (sentList.length) {
        setMessages((current) => {
          const next = [...current];
          for (const msg of sentList) {
            const mapped = mapSdkMessage(msg, sessionId, agentUserIdRef.current, visitorId);
            if (mapped.id && !next.some((message) => message.id === mapped.id)) {
              next.push(mapped);
            }
          }
          return next;
        });
      }
      setDraft('');
      clearPendingFiles();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not send attachment');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Box
      sx={{
        height: { xs: 'calc(100vh - 200px)', md: 'calc(100vh - 160px)' },
        display: 'flex',
        flexDirection: 'column',
        minHeight: 520,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(0, 122, 255, 0.14)',
          boxShadow: '0 12px 32px rgba(0, 122, 255, 0.10)',
          backgroundColor: '#ffffff',
        }}
      >
        {showRoomList && (
          <Box
            sx={{
              width: { xs: '100%', md: '30%' },
              minWidth: { md: 280 },
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'left',
              borderRight: { md: '1px solid rgba(0, 122, 255, 0.12)' },
              background: 'linear-gradient(180deg, #f7fbff 0%, #ffffff 120px)',
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 2,
                background: themeBackgrounds.default,
                color: '#ffffff',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                  Conversations
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Assigned and previous conversations
              </Typography>
            </Box>
            <Tabs
              value={listTab}
              onChange={(_, value: 'active' | 'hold' | 'closed') => setListTab(value)}
              variant="fullWidth"
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{
                minHeight: 0,
                m: 1,
                p: 0.4,
                borderRadius: 2,
                bgcolor: 'rgba(0, 122, 255, 0.06)',
                '& .MuiTab-root': {
                  minHeight: 34,
                  minWidth: 0,
                  py: 0.75,
                  px: 0.5,
                  borderRadius: 1.5,
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#64748b',
                  '&.Mui-selected': {
                    color: '#007aff',
                    bgcolor: '#fff',
                    boxShadow: '0 1px 4px rgba(0, 122, 255, 0.12)',
                  },
                },
              }}
            >
              {(
                [
                  {
                    value: 'active' as const,
                    label: 'Active',
                    count: activeTabRooms.length,
                    notify: rooms.some(
                      (r) =>
                        r.status !== 'closed' &&
                        r.status !== 'hold' &&
                        (r.unreadCount > 0 || r.status === 'assigned')
                    ),
                  },
                  {
                    value: 'hold' as const,
                    label: 'Hold',
                    count: holdTabRooms.length,
                    notify: rooms.some((r) => r.status === 'hold' && r.unreadCount > 0),
                  },
                  {
                    value: 'closed' as const,
                    label: 'Closed',
                    count: closedTabRooms.length,
                    notify: rooms.some((r) => r.status === 'closed' && r.unreadCount > 0),
                  },
                ]
              ).map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, position: 'relative' }}>
                      {tab.label}
                      {tab.count > 0 ? ` ${tab.count}` : ''}
                      {tab.notify && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: '#ef4444',
                            animation: 'agentTabBlink 1s ease-in-out infinite',
                            '@keyframes agentTabBlink': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.2 },
                            },
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1.5 }}>
              {inboxLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {!inboxLoading && tabRooms.length === 0 && (
                <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No assigned conversations yet. New handoffs will appear here.
                  </Typography>
                </Box>
              )}
              {tabRooms.map(({ room, badge }) => {
                  const isSelected = room.id === selectedRoomId;
                  const isNewChat = room.status === 'assigned' && !isSelected;
                  return (
                    <Box
                      key={room.id}
                      onClick={() => handleSelectRoom(room.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        textAlign: 'left',
                        gap: 1.5,
                        p: 1.5,
                        mb: 0.75,
                        borderRadius: 2.5,
                        cursor: 'pointer',
                        border: isSelected
                          ? '1px solid rgba(0, 122, 255, 0.28)'
                          : isNewChat
                            ? '1px solid rgba(59, 130, 246, 0.35)'
                            : '1px solid transparent',
                        background: isSelected
                          ? 'linear-gradient(135deg, rgba(0, 122, 255, 0.12) 0%, rgba(95, 176, 255, 0.18) 100%)'
                          : isNewChat
                            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.16) 0%, rgba(147, 197, 253, 0.28) 100%)'
                            : '#ffffff',
                        boxShadow: isSelected ? '0 8px 18px rgba(0, 122, 255, 0.10)'
                          : isNewChat
                            ? '0 4px 14px rgba(59, 130, 246, 0.14)'
                            : 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: isSelected || isNewChat ? undefined : '#f7fbff',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <CircleIcon sx={{ fontSize: 10, color: presenceColor[room.presence] }} />
                        }
                      >
                        <Avatar
                          sx={{
                            bgcolor: room.avatarColor,
                            color: '#1976d2',
                            fontWeight: 700,
                            width: 44,
                            height: 44,
                          }}
                        >
                          {getInitials(room.customerName)}
                        </Avatar>
                      </Badge>
                      <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                          <Typography noWrap sx={{ fontWeight: 700, fontSize: 14, flex: 1, textAlign: 'left' }}>
                            {room.customerName}
                          </Typography>
                          {badge ? (
                            <Chip
                              size="small"
                              label={badge}
                              sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 700,
                                backgroundColor:
                                  badge === 'Current'
                                    ? 'rgba(0, 122, 255, 0.14)'
                                    : badge === 'New'
                                      ? 'rgba(16, 185, 129, 0.14)'
                                      : 'rgba(107, 114, 128, 0.12)',
                                color:
                                  badge === 'Current'
                                    ? '#1d4ed8'
                                    : badge === 'New'
                                      ? '#047857'
                                      : '#4b5563',
                              }}
                            />
                          ) : null}
                          {room.status === 'closed' && !badge && (
                            <Chip
                              size="small"
                              label={statusChipStyles.closed.label}
                              sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 700,
                                backgroundColor: statusChipStyles.closed.bg,
                                color: statusChipStyles.closed.color,
                              }}
                            />
                          )}
                          {room.status === 'hold' && !badge && (
                            <Chip
                              size="small"
                              label={statusChipStyles.hold.label}
                              sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 700,
                                backgroundColor: statusChipStyles.hold.bg,
                                color: statusChipStyles.hold.color,
                              }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatListTime(room.lastMessageAt)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            noWrap
                            variant="body2"
                            color="text.secondary"
                            sx={{ flex: 1, fontWeight: room.unreadCount ? 600 : 400, textAlign: 'left' }}
                          >
                            {room.lastMessage}
                          </Typography>
                          {room.unreadCount > 0 && (
                            <Box
                              sx={{
                                minWidth: 20,
                                height: 20,
                                px: 0.6,
                                borderRadius: 10,
                                background: themeBackgrounds.default,
                                color: '#ffffff',
                                fontSize: 11,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {room.unreadCount}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        )}

        {showChatPane && (
          <Box
            onDragEnter={handleComposerDragEnter}
            onDragLeave={handleComposerDragLeave}
            onDragOver={handleComposerDragOver}
            onDrop={handleComposerDrop}
            sx={{
              width: { xs: '100%', md: '70%' },
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              backgroundColor: '#f8fbff',
              position: 'relative',
            }}
          >
            {isDraggingMedia && selectedRoom && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(244, 248, 255, 0.88)',
                  border: '2px dashed rgba(0, 122, 255, 0.45)',
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#007aff' }}>
                  Drop files to attach
                </Typography>
              </Box>
            )}
            {!selectedRoom ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 4,
                  textAlign: 'center',
                }}
              >
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    mb: 2,
                    background: themeBackgrounds.default,
                  }}
                >
                  <ChatBubbleOutlineIcon sx={{ fontSize: 34 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Select a conversation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
                  Choose a chat room from the left to view the thread and reply as a desk agent.
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    backgroundColor: '#ffffff',
                    borderBottom: '1px solid rgba(0, 122, 255, 0.12)',
                  }}
                >
                  {!isDesktop && (
                    <IconButton
                      onClick={() => {
                        const roomId = currentRoomIdRef.current;
                        try {
                          Sampark.PeerChat.leaveroom();
                        } catch { /* ignore */ }
                        try {
                          if (roomId) {
                            const socket = Sampark.getSocket?.();
                            socket?.emit?.('leaveRoom', roomId);
                          }
                          (Sampark.ChatBot as any).currentSessionId = null;
                          (Sampark.ChatBot as any).currentRoomId = null;
                          (Sampark.ChatBot as any).isBotChatActive = false;
                        } catch { /* ignore */ }
                        currentSessionIdRef.current = null;
                        currentRoomIdRef.current = null;
                        currentVisitorIdRef.current = null;
                        setSelectedRoomId(null);
                        setMessages([]);
                        setTypingUser(null);
                      }}
                      size="small"
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <CircleIcon sx={{ fontSize: 11, color: presenceColor[selectedRoom.presence] }} />
                    }
                  >
                    <Avatar
                      sx={{
                        bgcolor: selectedRoom.avatarColor,
                        color: '#1976d2',
                        fontWeight: 700,
                      }}
                    >
                      {getInitials(selectedRoom.customerName)}
                    </Avatar>
                  </Badge>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography noWrap sx={{ fontWeight: 700 }}>
                        {selectedRoom.customerName}
                      </Typography>
                      <Chip
                        size="small"
                        label={statusChipStyles[selectedRoom.status].label}
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          backgroundColor: statusChipStyles[selectedRoom.status].bg,
                          color: statusChipStyles[selectedRoom.status].color,
                        }}
                      />
                    </Box>
                  </Box>
                  <Tooltip title="Agent workspace">
                    <Chip
                      icon={<SupportAgentIcon sx={{ fontSize: 16, color: '#ffffff !important' }} />}
                      label="Desk Agent"
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        background: themeBackgrounds.default,
                        color: '#ffffff',
                        fontWeight: 600,
                      }}
                    />
                  </Tooltip>
                  <IconButton size="small" onClick={(event) => setHeaderMenuAnchor(event.currentTarget)}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={headerMenuAnchor}
                    open={Boolean(headerMenuAnchor)}
                    onClose={() => setHeaderMenuAnchor(null)}
                  >
                    {(selectedRoom.status === 'active' || selectedRoom.status === 'hold') && (
                      <MenuItem onClick={handleHoldOrResume}>
                        <ListItemIcon>
                          {selectedRoom.status === 'hold' ? (
                            <PlayCircleOutlineIcon fontSize="small" />
                          ) : (
                            <PauseCircleOutlineIcon fontSize="small" />
                          )}
                        </ListItemIcon>
                        {selectedRoom.status === 'hold' ? 'Resume' : 'Hold'}
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={handleEndChat}
                      disabled={
                        selectedRoom.status === 'closed' ||
                        pendingCloseSessionId === selectedRoom.id
                      }
                    >
                      <ListItemIcon>
                        <CallEndIcon fontSize="small" />
                      </ListItemIcon>
                      {pendingCloseSessionId === selectedRoom.id
                        ? 'Waiting for visitor…'
                        : 'End chat'}
                    </MenuItem>
                  </Menu>
                </Box>

                <Box
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
                    if (atBottomRef.current) setShowScrollToBottom(false);
                  }}
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: { xs: 1.5, md: 3 },
                    py: 2.5,
                    backgroundImage:
                      'radial-gradient(circle at 20% 20%, rgba(0, 122, 255, 0.05) 0, transparent 28%), radial-gradient(circle at 80% 0%, rgba(95, 176, 255, 0.08) 0, transparent 32%)',
                  }}
                >
                  {isOpeningSession && roomMessages.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  )}
                  {roomMessages.map((message) => {
                    if (message.messageType === 'system') {
                      const timeLabel = formatBubbleTime(message.sentAt);
                  
                      return (
                        <Box
                          key={message.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 1.5,
                            width: '100%',
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '90%',
                              px: 1.5,
                              py: 0.75,
                              borderRadius: '12px',
                              backgroundColor: '#f3f4f6',
                              color: '#4b5563',
                              fontSize: 12,
                              textAlign: 'center',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: 12,
                                lineHeight: 1.5,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {message.text}
                            </Typography>
                  
                            {timeLabel ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.25,
                                  fontSize: 9,
                                  opacity: 0.65,
                                }}
                              >
                                {timeLabel}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      );
                    }
                    const isAgent = message.sender === 'agent';
                    const timeLabel = formatBubbleTime(message.sentAt);
                    return (
                      <Box
                        key={message.id}
                        sx={{
                          display: 'flex',
                          justifyContent: isAgent ? 'flex-end' : 'flex-start',
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ maxWidth: { xs: '88%', md: '68%' }, minWidth: 0 }}>
                          <Box
                            sx={{
                              px: 1.75,
                              py: 1.25,
                              borderRadius: isAgent ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                              color: isAgent ? '#ffffff' : '#1e293b',
                              background: isAgent ? themeBackgrounds.default : '#ffffff',
                              border: isAgent ? 'none' : '1px solid rgba(0, 122, 255, 0.10)',
                              boxShadow: isAgent
                                ? '0 8px 18px rgba(0, 122, 255, 0.18)'
                                : '0 6px 16px rgba(15, 23, 42, 0.06)',
                              textAlign: 'left',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                            }}
                          >
                            {(() => {
                              const files =
                                message.files && message.files.length
                                  ? message.files
                                  : message.fileUrl
                                    ? [
                                        {
                                          attachmentUrl: message.fileUrl,
                                          originalName: message.fileName,
                                          fileId: message.fileId,
                                        },
                                      ]
                                    : [];
                              const caption =
                                message.text &&
                                message.text !== message.fileName &&
                                !files.some((f) => f.originalName && f.originalName === message.text)
                                  ? message.text
                                  : '';
                              const openPreview = (startIndex: number) => {
                                setMediaPreview({
                                  items: files,
                                  index: startIndex,
                                  caption: caption || undefined,
                                });
                              };

                              if (message.messageType === 'image' && files.length) {
                                const visible = files.slice(0, 3);
                                const leftover = files.length - visible.length;
                                return (
                                  <>
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                          files.length === 1 ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                                        gap: 0.75,
                                        mb: caption ? 1 : 0,
                                        maxWidth: 280,
                                      }}
                                    >
                                      {visible.map((file, idx) => {
                                        const isLastVisible = idx === visible.length - 1 && leftover > 0;
                                        return (
                                          <Box
                                            key={`${message.id}-img-${idx}`}
                                            onClick={() => openPreview(idx)}
                                            sx={{
                                              position: 'relative',
                                              borderRadius: 1.5,
                                              overflow: 'hidden',
                                              cursor: 'pointer',
                                              gridColumn:
                                                files.length === 3 && idx === 0 ? '1 / -1' : 'auto',
                                              aspectRatio: files.length === 1 ? '4 / 3' : '1 / 1',
                                              minHeight: files.length === 1 ? 160 : 96,
                                              backgroundColor: 'rgba(15,23,42,0.06)',
                                            }}
                                          >
                                            {file.attachmentUrl ? (
                                              <Box
                                                component="img"
                                                src={file.attachmentUrl}
                                                alt={file.originalName || 'image'}
                                                sx={{
                                                  position: 'absolute',
                                                  inset: 0,
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover',
                                                  display: 'block',
                                                }}
                                              />
                                            ) : null}
                                            {isLastVisible ? (
                                              <Box
                                                sx={{
                                                  position: 'absolute',
                                                  inset: 0,
                                                  backgroundColor: 'rgba(15,23,42,0.55)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  color: '#fff',
                                                  fontWeight: 700,
                                                  fontSize: 22,
                                                }}
                                              >
                                                +{leftover}
                                              </Box>
                                            ) : null}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                    {caption ? (
                                      <Typography
                                        variant="body2"
                                        sx={{ lineHeight: 1.55, whiteSpace: 'pre-wrap', textAlign: 'left' }}
                                      >
                                        {caption}
                                      </Typography>
                                    ) : null}
                                  </>
                                );
                              }

                              if (
                                message.messageType &&
                                message.messageType !== 'text' &&
                                message.messageType !== 'image' &&
                                files.length
                              ) {
                                const visible = files.slice(0, 3);
                                const leftover = files.length - visible.length;
                                return (
                                  <>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: caption ? 1 : 0 }}>
                                      {visible.map((file, idx) => {
                                        const isLastVisible = idx === visible.length - 1 && leftover > 0;
                                        return (
                                          <Box
                                            key={`${message.id}-file-${idx}`}
                                            onClick={() => {
                                              if (file.attachmentUrl) {
                                                window.open(file.attachmentUrl, '_blank', 'noopener,noreferrer');
                                              } else {
                                                openPreview(idx);
                                              }
                                            }}
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              px: 1.25,
                                              py: 1,
                                              borderRadius: 1.5,
                                              backgroundColor: isAgent
                                                ? 'rgba(255,255,255,0.16)'
                                                : 'rgba(0, 122, 255, 0.08)',
                                              border: isAgent
                                                ? '1px solid rgba(255,255,255,0.22)'
                                                : '1px solid rgba(0, 122, 255, 0.14)',
                                              cursor: 'pointer',
                                              position: 'relative',
                                            }}
                                          >
                                            <InsertDriveFileOutlinedIcon
                                              sx={{ fontSize: 22, opacity: 0.9, flexShrink: 0 }}
                                            />
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                flex: 1,
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {file.originalName || 'Attachment'}
                                            </Typography>
                                            {isLastVisible ? (
                                              <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0 }}>
                                                +{leftover}
                                              </Typography>
                                            ) : null}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                    {caption ? (
                                      <Typography
                                        variant="body2"
                                        sx={{ lineHeight: 1.55, whiteSpace: 'pre-wrap', textAlign: 'left' }}
                                      >
                                        {caption}
                                      </Typography>
                                    ) : null}
                                  </>
                                );
                              }

                              if (!message.messageType || message.messageType === 'text' || caption) {
                                return (
                                  <Typography
                                    variant="body2"
                                    sx={{ lineHeight: 1.55, whiteSpace: 'pre-wrap', textAlign: 'left' }}
                                  >
                                    {message.text}
                                  </Typography>
                                );
                              }
                              return null;
                            })()}
                            {timeLabel ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  opacity: 0.75,
                                  fontSize: 10,
                                  textAlign: isAgent ? 'right' : 'left',
                                }}
                              >
                                {timeLabel}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                  {typingUser && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.75,
                          py: 1.25,
                          borderRadius: '18px 18px 18px 6px',
                          backgroundColor: '#ffffff',
                          border: '1px solid rgba(0, 122, 255, 0.10)',
                          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.06)',
                          '@keyframes agentChatTypingDot': {
                            '0%, 80%, 100%': { opacity: 0.35, transform: 'translateY(0)' },
                            '40%': { opacity: 1, transform: 'translateY(-3px)' },
                          },
                          '& span': {
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: '#94a3b8',
                            animation: 'agentChatTypingDot 1.2s infinite ease-in-out',
                          },
                          '& span:nth-of-type(2)': { animationDelay: '0.2s' },
                          '& span:nth-of-type(3)': { animationDelay: '0.4s' },
                        }}
                      >
                        <span />
                        <span />
                        <span />
                      </Box>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                  {showScrollToBottom && (
                    <IconButton
                      aria-label="Scroll to latest messages"
                      onClick={() => {
                        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                        atBottomRef.current = true;
                        setShowScrollToBottom(false);
                      }}
                      sx={{
                        position: 'sticky',
                        bottom: 8,
                        float: 'right',
                        width: 40,
                        height: 40,
                        backgroundColor: '#ffffff',
                        color: '#007aff',
                        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.18)',
                        border: '1px solid rgba(0, 122, 255, 0.16)',
                        '@keyframes agentScrollDownPulse': {
                          '0%, 100%': {
                            transform: 'translateY(0)',
                            boxShadow:
                              '0 4px 14px rgba(15, 23, 42, 0.18), 0 0 0 0 rgba(0, 122, 255, 0.35)',
                          },
                          '50%': {
                            transform: 'translateY(4px)',
                            boxShadow:
                              '0 8px 18px rgba(15, 23, 42, 0.22), 0 0 16px 4px rgba(0, 122, 255, 0.45)',
                          },
                        },
                        '@keyframes agentScrollDownIn': {
                          from: { opacity: 0, transform: 'scale(0.85)' },
                          to: { opacity: 1, transform: 'scale(1)' },
                        },
                        animation:
                          'agentScrollDownIn 0.2s ease-out, agentScrollDownPulse 1.2s ease-in-out 0.2s infinite',
                        '&:hover': { backgroundColor: '#f8fbff' },
                      }}
                    >
                      <KeyboardArrowDownIcon />
                    </IconButton>
                  )}
                </Box>

                <Box
                  sx={{
                    px: { xs: 1.5, md: 2.5 },
                    py: 1.75,
                    backgroundColor: '#ffffff',
                    borderTop: '1px solid rgba(0, 122, 255, 0.12)',
                  }}
                >
                  {selectedRoom.status === 'assigned' && (
                    <Box sx={{ mb: 1.25, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={handleRejectJoin}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          color: '#dc6b6b',
                          borderColor: 'rgba(239, 68, 68, 0.35)',
                          backgroundColor: 'rgba(239, 68, 68, 0.08)',
                          '&:hover': {
                            borderColor: 'rgba(239, 68, 68, 0.5)',
                            backgroundColor: 'rgba(239, 68, 68, 0.14)',
                          },
                        }}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleApproveJoin}
                        sx={{
                          background: themeBackgrounds.default,
                          textTransform: 'none',
                          fontWeight: 700,
                          boxShadow: 'none',
                        }}
                      >
                        Approve
                      </Button>
                    </Box>
                  )}
                  <Box
                    sx={{
                      borderRadius: 4,
                      backgroundColor: '#f4f8ff',
                      border: '1px solid rgba(0, 122, 255, 0.16)',
                      overflow: 'hidden',
                    }}
                  >
                    {pendingFiles.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1.25, pt: 1.25 }}>
                        {pendingFiles.map((file, idx) => (
                          <Box
                            key={`pending-${idx}-${file.name}`}
                            sx={{
                              position: 'relative',
                              width: 56,
                              height: 56,
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              backgroundColor: '#e2e8f0',
                              border: '1px solid rgba(15,23,42,0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 0.5,
                            }}
                          >
                            {file.type.startsWith('image/') && pendingPreviewUrls[idx] ? (
                              <Box
                                component="img"
                                src={pendingPreviewUrls[idx]}
                                alt={file.name}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: 9,
                                  textAlign: 'center',
                                  color: '#475569',
                                  wordBreak: 'break-all',
                                  lineHeight: 1.2,
                                }}
                              >
                                {file.name.slice(0, 18)}
                              </Typography>
                            )}
                            <IconButton
                              size="small"
                              aria-label="Remove attachment"
                              onClick={() =>
                                setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
                              }
                              disabled={isSending}
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                width: 18,
                                height: 18,
                                p: 0,
                                backgroundColor: 'rgba(15,23,42,0.75)',
                                color: '#fff',
                                '&:hover': { backgroundColor: 'rgba(15,23,42,0.9)' },
                              }}
                            >
                              ×
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.5,
                      }}
                    >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      hidden
                      onChange={handleFileSelected}
                    />
                    <IconButton
                      size="small"
                      onClick={handlePickFile}
                      disabled={!composerUnlocked || isSending}
                    >
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                    <InputBase
                      value={draft}
                      onChange={(event) => {
                        const text = event.target.value;
                        setDraft(text);
                        const visitorId = currentVisitorIdRef.current || selectedRoom?.visitorId;
                        if (!composerUnlocked || !visitorId) return;
                        const typingIndicator = new Sampark.TypingIndicator(visitorId, Sampark.RECEIVER_TYPE.USER);
                        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                        try {
                          if (text) {
                            Sampark.ChatBot.startTyping(typingIndicator);
                            typingTimeoutRef.current = setTimeout(() => {
                              try { Sampark.ChatBot.endTyping(typingIndicator); } catch { /* ignore */ }
                            }, 1000);
                          } else {
                            Sampark.ChatBot.endTyping(typingIndicator);
                          }
                        } catch { /* ignore */ }
                      }}
                      onPaste={handleComposerPaste}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder={
                        selectedRoom.status === 'assigned'
                          ? 'Approve the handoff to reply...'
                          : selectedRoom.status === 'hold'
                            ? 'On hold — resume to reply...'
                            : selectedRoom.status === 'reconnecting'
                            ? 'Reconnecting...'
                            : selectedRoom.status === 'closed'
                              ? 'This conversation is closed'
                              : pendingFiles.length
                                ? 'Add a caption...'
                                : 'Write a reply...'
                      }
                      disabled={!composerUnlocked || isSending}
                      multiline
                      maxRows={4}
                      sx={{ flex: 1, py: 1, fontSize: 14 }}
                    />
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={(!draft.trim() && !pendingFiles.length) || !composerUnlocked || isSending}
                      sx={{
                        background: themeBackgrounds.default,
                        color: '#ffffff',
                        width: 42,
                        height: 42,
                        '&:hover': {
                          background: themeBackgrounds.default,
                          opacity: 0.9,
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#dbe7f5',
                          color: '#94a3b8',
                        },
                      }}
                    >
                      <SendRoundedIcon fontSize="small" />
                    </IconButton>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      {mediaPreview && mediaPreview.items.length > 0 && (
        <Box
          onClick={() => setMediaPreview(null)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            backgroundColor: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: 'min(920px, 96vw)',
              width: '100%',
              position: 'relative',
            }}
          >
            {mediaPreview.items.length > 1 ? (
              <>
                <IconButton
                  aria-label="Previous media"
                  onClick={() =>
                    setMediaPreview((prev) =>
                      prev
                        ? {
                            ...prev,
                            index: (prev.index - 1 + prev.items.length) % prev.items.length,
                          }
                        : prev
                    )
                  }
                  sx={{
                    position: 'absolute',
                    left: { xs: 0, md: -48 },
                    top: '40%',
                    color: '#fff',
                    backgroundColor: 'rgba(15,23,42,0.45)',
                    '&:hover': { backgroundColor: 'rgba(15,23,42,0.65)' },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  aria-label="Next media"
                  onClick={() =>
                    setMediaPreview((prev) =>
                      prev
                        ? {
                            ...prev,
                            index: (prev.index + 1) % prev.items.length,
                          }
                        : prev
                    )
                  }
                  sx={{
                    position: 'absolute',
                    right: { xs: 0, md: -48 },
                    top: '40%',
                    color: '#fff',
                    backgroundColor: 'rgba(15,23,42,0.45)',
                    '&:hover': { backgroundColor: 'rgba(15,23,42,0.65)' },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            ) : null}
            {(() => {
              const current = mediaPreview.items[mediaPreview.index] || mediaPreview.items[0];
              const mime = String(current.mimeType || '');
              const name = String(current.originalName || '');
              const isImage =
                mime.startsWith('image/') ||
                /\.(png|jpe?g|gif|webp|bmp)($|\?)/i.test(name);
              if (isImage && current.attachmentUrl) {
                return (
                  <Box
                    component="img"
                    src={current.attachmentUrl}
                    alt={current.originalName || 'preview'}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: mediaPreview.caption ? '80vh' : '90vh',
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                );
              }
              return (
                <Box
                  sx={{
                    minWidth: 280,
                    maxWidth: 420,
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 36, color: '#007aff' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                      {current.originalName || 'Attachment'}
                    </Typography>
                    {current.attachmentUrl ? (
                      <Button
                        size="small"
                        href={current.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mt: 1, px: 0 }}
                      >
                        Open file
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              );
            })()}
            {mediaPreview.items.length > 1 ? (
              <Typography variant="caption" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
                {mediaPreview.index + 1} / {mediaPreview.items.length}
              </Typography>
            ) : null}
            {mediaPreview.caption ? (
              <Typography
                variant="body2"
                sx={{
                  mt: 1.5,
                  color: '#ffffff',
                  textAlign: 'center',
                  whiteSpace: 'pre-wrap',
                  maxWidth: '100%',
                }}
              >
                {mediaPreview.caption}
              </Typography>
            ) : null}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AgentChats;
